import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';
import { ErrorBanner, SortTh, lower, sortRows, useGridSort } from './Configuration';
import { FilterCount, MultiSelect, SearchBox, anyFilterSet, matchesSelection } from './registerFilters';
import {
  USER_ACTION_BADGE,
  USER_ACTION_LABELS,
  downloadImportTemplate,
  readImportFile,
  toCommitRow,
} from './externalUserImport';

// Admin-only management screen for customer contacts (Configuration > Customer Access). Redesigned
// 2026-09-04 alongside Phase E (Jeremy): a filterable, county-grouped grid in place of one card per
// contact, a real delete next to Disable, and a spreadsheet import that can seed both this list and
// the Project Contact List in one pass. There is no Entra step for a customer contact at all --
// local credentials (ADR 0035) replaced that plan before this screen shipped.

const STATUS_LABELS = { active: 'Active', disabled: 'Disabled', pending: 'Not yet signed up', locked: 'Locked' };
const STATUS_BADGE = { active: 'badge-green', disabled: 'badge-yellow', pending: 'badge-yellow', locked: 'badge-blue' };
const STATUS_OPTIONS = Object.keys(STATUS_LABELS).map((value) => ({ value, label: STATUS_LABELS[value] }));

export function statusOf(user) {
  if (!user.isActive) return 'disabled';
  if (user.lockedUntil && new Date(user.lockedUntil) > new Date()) return 'locked';
  if (!user.passwordUpdatedAt) return 'pending';
  return 'active';
}

/// Every county a contact has any project access in, sorted -- most contacts have exactly one, but
/// nothing stops a state-level contact from spanning several, and the filter/grouping below both
/// need to treat that contact as belonging to all of them, not just the first.
export function countiesOf(user) {
  const names = new Set((user.projectAccess || []).map((g) => g.project?.account?.name).filter(Boolean));
  return [...names].sort();
}

export const BLANK_EU_FILTERS = { counties: [], statuses: [], text: '' };
const FILTER_ARRAY_KEYS = ['counties', 'statuses'];

export function filterExternalUsers(users, filters) {
  const needle = (filters.text || '').trim().toLowerCase();
  return (users || []).filter((u) => {
    const counties = countiesOf(u);
    if (filters.counties.length && !filters.counties.some((c) => counties.includes(c))) return false;
    if (!matchesSelection(filters.statuses, statusOf(u), u)) return false;
    if (needle && !`${u.name} ${u.email}`.toLowerCase().includes(needle)) return false;
    return true;
  });
}

export function isExternalUserFiltered(filters) {
  return anyFilterSet(filters, FILTER_ARRAY_KEYS);
}

/// Groups for display -- a contact with grants in more than one county appears once per county
/// (repeats, not a single "multiple" bucket), since each occurrence is a real fact about that
/// county's roster. "Unassigned" (no project access yet) always sorts last.
export function groupByCounty(users) {
  const groups = new Map();
  for (const u of users) {
    const counties = countiesOf(u);
    for (const key of counties.length ? counties : ['Unassigned']) {
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(u);
    }
  }
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b);
  });
}

const SORT_VALUE = {
  name: (r) => lower(r.name),
  email: (r) => lower(r.email),
  status: (r) => lower(STATUS_LABELS[statusOf(r)]),
  lastLoginAt: (r) => r.lastLoginAt || '',
};

export default function ExternalUsers() {
  const { currentUser } = useApp();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState({});
  const [inviteSent, setInviteSent] = useState({});
  const [filters, setFilters] = useState({ ...BLANK_EU_FILTERS });
  const [sort, toggleSort] = useGridSort('name');
  const [importOpen, setImportOpen] = useState(false);

  const isAdmin = !currentUser || currentUser.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [userRows, projectRows] = await Promise.all([
        apiFetch('/external-users'),
        apiFetch('/projects'),
      ]);
      setUsers(userRows);
      setProjects(projectRows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const counties = useMemo(() => {
    const names = new Set();
    users.forEach((u) => countiesOf(u).forEach((c) => names.add(c)));
    return [...names].sort();
  }, [users]);

  const filtered = useMemo(() => filterExternalUsers(users, filters), [users, filters]);
  const grouped = useMemo(() => {
    const sorted = sortRows(filtered, sort, (row, key) => SORT_VALUE[key](row));
    return groupByCounty(sorted);
  }, [filtered, sort]);

  async function createUser(e) {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim()) return;
    setCreating(true);
    setActionError('');
    try {
      await apiFetch('/external-users', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), email: newEmail.trim() }),
      });
      setNewName('');
      setNewEmail('');
      await load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setCreating(false);
    }
  }

  // Shared by every action below that's just "call the API, then refresh the list, surfacing any
  // failure the same way" -- the only thing that ever differed between them was the request itself.
  async function runAction(request) {
    setActionError('');
    try {
      await request();
      await load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  const toggleActive = (user) => runAction(() => apiFetch(`/external-users/${user.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive: !user.isActive }),
  }));

  function deleteUser(user) {
    if (!window.confirm(
      `Permanently delete ${user.name} (${user.email})? This removes their sign-in, sessions and ` +
      `project access, and can't be undone. Use Disable instead if you might want them back.`,
    )) return;
    return runAction(() => apiFetch(`/external-users/${user.id}`, { method: 'DELETE' }));
  }

  const grantProject = (userId, projectId) => runAction(() => apiFetch(`/external-users/${userId}/projects`, {
    method: 'POST',
    body: JSON.stringify({ projectId }),
  }));

  const revokeProject = (userId, projectId) => runAction(
    () => apiFetch(`/external-users/${userId}/projects/${projectId}`, { method: 'DELETE' }),
  );

  // Registers/calendar write access, not read access -- read access is the grant itself.
  const setProjectEditable = (userId, projectId, canEdit) => runAction(
    () => apiFetch(`/external-users/${userId}/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify({ canEdit }),
    }),
  );

  // Sends the email that lets a contact set their first password (ADR 0035). Not automatic on
  // create: an admin normally grants projects first, so the portal isn't empty when they arrive.
  // Re-inviting is safe -- the new link invalidates the old one.
  async function sendInvite(user) {
    setActionError('');
    setInviteSent((sent) => ({ ...sent, [user.id]: false }));
    setInviting((busy) => ({ ...busy, [user.id]: true }));
    try {
      await apiFetch(`/external-users/${user.id}/invite`, { method: 'POST' });
      setInviteSent((sent) => ({ ...sent, [user.id]: true }));
    } catch (e) {
      setActionError(e.message);
    } finally {
      setInviting((busy) => ({ ...busy, [user.id]: false }));
    }
  }

  return (
    <div>
      {/* One section of the Administration hub (Administration.js) -- role gate and page-header live
          there now. `isAdmin` above still guards the data load: defense in depth against a stray
          direct hit on this component, same reasoning @Roles('admin') on the API is the real edge. */}
      <p className="page-subtitle" style={{ marginBottom: 20 }}>
        Who can sign in to the customer portal at <code>/portal</code>, and which project(s) they
        can see there. Customer contacts sign in with their own email and password.
      </p>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Add a customer contact</span>
          <button type="button" className="btn btn-outline" onClick={() => setImportOpen(true)}>
            Import from spreadsheet
          </button>
        </div>
        <form onSubmit={createUser} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
            <label className="form-label" htmlFor="new-contact-name">Name</label>
            <input
              id="new-contact-name" className="form-input" value={newName}
              onChange={(e) => setNewName(e.target.value)} placeholder="Jeremy Bromm"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 260 }}>
            <label className="form-label" htmlFor="new-contact-email">Email</label>
            <input
              id="new-contact-email" type="email" className="form-input" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)} placeholder="jeremy@customer.com"
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={creating || !newName.trim() || !newEmail.trim()}>
            {creating ? 'Adding…' : 'Add contact'}
          </button>
        </form>
        <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 10 }}>
          This creates the record that grants access — it doesn't send an invite. Grant the
          project(s) they should see below, then use Send invitation when ready. Onboarding a whole
          county at once? Use Import from spreadsheet instead.
        </div>
      </div>

      {importOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setImportOpen(false)}>
          <div className="modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Import from spreadsheet</span>
              <button className="modal-close" onClick={() => setImportOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <ImportPanel onImported={load} />
            </div>
          </div>
        </div>
      )}

      <ErrorBanner error={error || actionError} />

      {loading ? (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
      ) : !users.length ? (
        <div className="placeholder-page">
          <div className="placeholder-icon">👤</div>
          <div className="placeholder-title">No customer contacts yet</div>
          <div className="placeholder-sub">Add one above, or import a county's user list.</div>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <MultiSelect
                label="County"
                options={counties.map((c) => ({ value: c, label: c }))}
                selected={filters.counties}
                onChange={(counties) => setFilters((f) => ({ ...f, counties }))}
                allLabel="All counties"
              />
              <MultiSelect
                label="Status"
                options={STATUS_OPTIONS}
                selected={filters.statuses}
                onChange={(statuses) => setFilters((f) => ({ ...f, statuses }))}
                allLabel="All statuses"
              />
              <SearchBox
                value={filters.text}
                onChange={(text) => setFilters((f) => ({ ...f, text }))}
                placeholder="Name or email…"
              />
            </div>
          </div>

          {isExternalUserFiltered(filters) && <FilterCount shown={filtered.length} total={users.length} noun="contact" />}

          {!filtered.length ? (
            <div className="placeholder-page">
              <div className="placeholder-icon">🔍</div>
              <div className="placeholder-title">No contacts match these filters</div>
              <div className="placeholder-sub">{users.length} contacts total — clear the filters to see them.</div>
            </div>
          ) : (
            grouped.map(([county, rows]) => (
              <CountyGroup
                key={county}
                county={county}
                rows={rows}
                projects={projects}
                sort={sort}
                onSort={toggleSort}
                inviting={inviting}
                inviteSent={inviteSent}
                onSendInvite={sendInvite}
                onToggleActive={toggleActive}
                onDelete={deleteUser}
                onGrant={grantProject}
                onRevoke={revokeProject}
                onSetEditable={setProjectEditable}
              />
            ))
          )}
        </>
      )}
    </div>
  );
}

function CountyGroup({ county, rows, projects, sort, onSort, inviting, inviteSent, onSendInvite, onToggleActive, onDelete, onGrant, onRevoke, onSetEditable }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="card" style={{ marginBottom: 16, padding: 0 }}>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', cursor: 'pointer', borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          <span style={{ display: 'inline-block', width: 14, opacity: 0.5 }}>{open ? '▾' : '▸'}</span>
          {county}
        </div>
        <span className="badge badge-gray">{rows.length} {rows.length === 1 ? 'contact' : 'contacts'}</span>
      </div>
      {open && (
        <div className="sheet-wrap" style={{ border: 'none', borderRadius: 0 }}>
          <table className="sheet">
            <thead>
              <tr>
                <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} style={{ minWidth: 160 }} />
                <SortTh label="Email" sortKey="email" sort={sort} onSort={onSort} style={{ minWidth: 220 }} />
                <th style={{ minWidth: 200 }}>Projects</th>
                <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} style={{ width: 130 }} />
                <SortTh label="Last sign-in" sortKey="lastLoginAt" sort={sort} onSort={onSort} style={{ width: 130 }} />
                <th style={{ width: 260 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <tr key={user.id}>
                  <td className="cell-read" style={{ fontWeight: 600 }}>{user.name}</td>
                  <td className="cell-read">{user.email}</td>
                  <td className="cell-read">
                    <ProjectAccessCell user={user} projects={projects} onGrant={onGrant} onRevoke={onRevoke} onSetEditable={onSetEditable} />
                  </td>
                  <td className="cell-read">
                    <span className={`badge ${STATUS_BADGE[statusOf(user)]}`}>{STATUS_LABELS[statusOf(user)]}</span>
                  </td>
                  <td className="cell-read">
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="cell-read">
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-outline"
                        style={{ fontSize: 11, padding: '3px 8px' }}
                        onClick={() => onSendInvite(user)}
                        disabled={!user.isActive || inviting[user.id]}
                        title={
                          user.isActive
                            ? 'Emails a single-use link to set a password. Any previous link stops working.'
                            : 'Enable this contact before inviting them.'
                        }
                      >
                        {inviting[user.id]
                          ? 'Sending…'
                          : inviteSent[user.id]
                            ? 'Sent ✓'
                            : user.passwordUpdatedAt ? 'Send reset' : 'Send invitation'}
                      </button>
                      <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => onToggleActive(user)}>
                        {user.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        className="btn btn-outline"
                        style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red)', borderColor: 'rgba(239,65,54,0.4)' }}
                        onClick={() => onDelete(user)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProjectAccessCell({ user, projects, onGrant, onRevoke, onSetEditable }) {
  const [adding, setAdding] = useState(false);
  const [projectId, setProjectId] = useState('');
  const granted = user.projectAccess || [];
  const available = projects.filter((p) => !granted.some((g) => g.project.id === p.id));

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {granted.map((g) => (
        <span key={g.project.id} className="badge badge-blue" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {g.project.name}
          <label
            title="Can edit this project's registers and calendar in the portal"
            style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 10, fontWeight: 400 }}
          >
            <input
              type="checkbox"
              checked={!!g.canEdit}
              onChange={(e) => onSetEditable(user.id, g.project.id, e.target.checked)}
            />
            Edit
          </label>
          <button
            onClick={() => onRevoke(user.id, g.project.id)}
            title={`Remove access to ${g.project.name}`}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontSize: 13, lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </span>
      ))}
      {!granted.length && !adding && (
        <span style={{ fontSize: 12, color: 'var(--text-light)', fontStyle: 'italic' }}>No projects granted</span>
      )}
      {adding ? (
        <>
          <select
            className="form-input" style={{ maxWidth: 180, fontSize: 12, padding: '2px 6px' }}
            value={projectId} onChange={(e) => setProjectId(e.target.value)} autoFocus
          >
            <option value="">— Choose a project —</option>
            {available.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button
            className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
            disabled={!projectId}
            onClick={() => { onGrant(user.id, projectId); setProjectId(''); setAdding(false); }}
          >
            Grant
          </button>
          <button
            type="button" title="Cancel"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-mid)', fontSize: 14, lineHeight: 1, padding: 0 }}
            onClick={() => { setAdding(false); setProjectId(''); }}
          >
            ×
          </button>
        </>
      ) : (
        available.length > 0 && (
          <button
            type="button" title="Grant a project"
            style={{
              border: '1px dashed var(--border)', background: 'none', cursor: 'pointer',
              color: 'var(--primary-light)', fontSize: 13, lineHeight: 1, padding: '2px 6px', borderRadius: 4,
            }}
            onClick={() => setAdding(true)}
          >
            +
          </button>
        )
      )}
    </div>
  );
}

/// The Phase E spreadsheet import. Two API calls, no upload: `readImportFile` parses the .xlsx
/// client-side, `preview` sends the plain rows for the API to evaluate against the live database,
/// and `commit` re-evaluates and applies them -- the same shape, run twice, so nothing here trusts
/// a preview that might be minutes stale by the time it's confirmed.
function ImportPanel({ onImported }) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState(null);
  const [addToProjectContacts, setAddToProjectContacts] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // lets the same file be picked again after a fix
    if (!file) return;
    setError('');
    setPreview(null);
    setResult(null);
    setFileName(file.name);
    try {
      const parsed = await readImportFile(file);
      if (!parsed.length) {
        setError('No rows found — check the spreadsheet has a header row plus at least one data row.');
        setRows(null);
        return;
      }
      setRows(parsed);
    } catch (err) {
      setError(err.message);
      setRows(null);
    }
  }

  async function runPreview() {
    setPreviewing(true);
    setError('');
    setResult(null);
    try {
      const result = await apiFetch('/external-users/import/preview', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      });
      setPreview(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function runCommit() {
    setCommitting(true);
    setError('');
    try {
      const commitResult = await apiFetch('/external-users/import/commit', {
        method: 'POST',
        body: JSON.stringify({ rows: preview.rows.map(toCommitRow), addToProjectContacts }),
      });
      setResult(commitResult);
      setPreview(null);
      setRows(null);
      setFileName('');
      onImported();
    } catch (err) {
      setError(err.message);
    } finally {
      setCommitting(false);
    }
  }

  const importableCount = preview ? preview.rows.filter((r) => r.errors.length === 0).length : 0;

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 14 }}>
        Bring in a county's user list at once: name, email and the project they should see. Each row
        creates (or updates) a customer contact and grants that project — the same result as adding
        them one at a time above.
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" className="btn btn-outline" onClick={downloadImportTemplate}>
          Download template
        </button>
        <label className="btn btn-outline" style={{ cursor: 'pointer', margin: 0 }}>
          Choose file…
          <input type="file" accept=".xlsx" onChange={handleFile} style={{ display: 'none' }} />
        </label>
        {fileName && <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>{fileName}</span>}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
        <input
          type="checkbox" checked={addToProjectContacts}
          onChange={(e) => setAddToProjectContacts(e.target.checked)}
        />
        Also add these people to the Project Contact List for their project
      </label>

      {error && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 14 }}>⚠ {error}</div>}

      {result && (
        <div style={{ fontSize: 13, color: 'var(--green)', marginBottom: 14, padding: '10px 12px', background: 'rgba(0,124,100,0.06)', borderRadius: 8 }}>
          Imported: {result.newContacts} new contact{result.newContacts === 1 ? '' : 's'}, {result.newGrants} new
          project grant{result.newGrants === 1 ? '' : 's'}
          {addToProjectContacts ? `, ${result.newProjectContacts} added to the Project Contact List` : ''}.
          {result.skippedRows > 0 && ` ${result.skippedRows} row${result.skippedRows === 1 ? '' : 's'} skipped due to errors.`}
        </div>
      )}

      {!preview && rows && (
        <button className="btn btn-primary" onClick={runPreview} disabled={previewing}>
          {previewing ? 'Checking…' : `Preview import (${rows.length} row${rows.length === 1 ? '' : 's'})`}
        </button>
      )}

      {preview && (
        <>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            {preview.summary.totalRows} rows: {preview.summary.newContacts} new contacts,{' '}
            {preview.summary.newGrants} new project grants
            {addToProjectContacts ? `, ${preview.summary.newProjectContacts} new contact-list entries` : ''}
            {preview.summary.errorRows > 0 && (
              <span style={{ color: 'var(--red)' }}> — {preview.summary.errorRows} row{preview.summary.errorRows === 1 ? '' : 's'} with errors</span>
            )}
          </div>
          <div className="sheet-wrap" style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 14 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Row</th>
                  <th style={{ minWidth: 140 }}>Name</th>
                  <th style={{ minWidth: 200 }}>Email</th>
                  <th style={{ minWidth: 180 }}>Project</th>
                  <th style={{ minWidth: 110 }}>County</th>
                  <th style={{ minWidth: 220 }}>Result</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.rowNumber}>
                    <td className="cell-read">{r.rowNumber}</td>
                    <td className="cell-read">{r.name || '—'}</td>
                    <td className="cell-read">{r.email || '—'}</td>
                    <td className="cell-read">{r.projectName || '—'}</td>
                    {/* Worth its own column, not folded into Project: with two accounts able to
                        name a project the same thing, this is how the admin confirms a row
                        resolved to the right county before granting anything. */}
                    <td className="cell-read">{r.accountName || '—'}</td>
                    <td className="cell-read">
                      {r.errors.length ? (
                        <span title={r.errors.join('; ')} className={`badge ${USER_ACTION_BADGE.skip}`}>
                          {r.errors[0]}
                        </span>
                      ) : (
                        <span className={`badge ${USER_ACTION_BADGE[r.userAction]}`}>
                          {USER_ACTION_LABELS[r.userAction]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-primary" onClick={runCommit}
              disabled={committing || importableCount === 0}
              title={importableCount === 0 ? 'No rows without errors to import' : undefined}
            >
              {committing ? 'Importing…' : `Confirm import (${importableCount} row${importableCount === 1 ? '' : 's'})`}
            </button>
            <button className="btn btn-outline" onClick={() => { setPreview(null); }} disabled={committing}>
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
}
